/**
 * Address with display name, explorer link, copy button and (in a token job)
 * a link to the family page. Explorer URL from the job's templates or the
 * server-provided `urls`.
 */
import { Link, useSearchParams } from "react-router";
import type { Urls, Who } from "@/api/types";
import { explorerUrl, routes } from "@/lib/jobs";
import { shortAddress } from "@/lib/format";
import { CopyButton } from "./CopyButton";

export interface AddressLinkProps {
  address: string;
  who?: Who;
  /** explicit name overrides who.name */
  name?: string;
  urls?: Urls;
  /** explorer template ("…/address/{address}") when `urls` is absent */
  explorerTemplate?: string;
  /** token job the family page lives in */
  job?: { chain: string; token: string };
  /** "family" links to the family page (default), "none" renders text only */
  link?: "family" | "none";
  /** where the family link goes instead of the address's family (e.g. one member's page) */
  to?: string;
  showBadges?: boolean;
  className?: string;
  selected?: boolean;
  onSelect?: (address: string, e: React.MouseEvent) => void;
}

export function AddressLink({
  address,
  who,
  name,
  urls,
  explorerTemplate,
  job,
  link = "family",
  to,
  showBadges = true,
  className = "",
  selected = false,
  onSelect,
}: AddressLinkProps) {
  const [params] = useSearchParams();
  const inherit = params.get("inherit_transfers");
  const basisSearch = inherit === "1" || inherit === "true" ? "?inherit_transfers=1" : "";
  const label = name ?? who?.name ?? shortAddress(address);
  const isShort = who?.name_source === "short" || (!name && !who);
  const explorer = urls?.address ?? who?.urls.address ?? explorerUrl(explorerTemplate, address);
  const family = who?.family ?? address;
  const inner = (
    <span
      className={`${isShort ? "num" : ""} ${selected ? "rounded-sm bg-accent/20 px-0.5" : ""}`}
      title={address}
    >
      {label}
    </span>
  );
  return (
    <span className={`inline-flex max-w-full items-center gap-1 ${className}`}>
      {link === "family" && job ? (
        <Link
          to={to ?? `${routes.family(job.chain, job.token, family)}${basisSearch}`}
          className="truncate text-fg hover:text-accent"
          onClick={(e) => {
            if (onSelect && (e.shiftKey || e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onSelect(family, e);
            }
          }}
        >
          {inner}
        </Link>
      ) : (
        inner
      )}
      {showBadges && who && <WhoBadges who={who} />}
      {explorer && (
        <a
          href={explorer}
          target="_blank"
          rel="noreferrer"
          className="text-fg-faint hover:text-accent"
          title="open in explorer"
          aria-label="explorer"
        >
          ↗
        </a>
      )}
      <CopyButton text={address} />
    </span>
  );
}

export function WhoBadges({ who }: { who: Who }) {
  const badges: { t: string; title: string; cls: string }[] = [];
  if (who.depth > 0)
    badges.push({
      t: `d${who.depth}`,
      title: `linked, ${who.depth} hop(s) from ${who.family}`,
      cls: "text-fg-muted",
    });
  if (who.role)
    badges.push({
      t: who.role,
      // Every role that applies, widest first, each saying where it came
      // from — the effective one is the narrowest of them.
      title: who.roles?.length
        ? who.roles.map((r) => `${r.role} — ${r.scope} (${r.source})`).join("\n")
        : `role ${who.role}`,
      cls: "text-accent",
    });
  if (who.is_venue) badges.push({ t: who.venue_kind ?? "venue", title: "venue", cls: "text-liquidity" });
  else if (who.is_contract) badges.push({ t: "contract", title: "contract", cls: "text-fg-muted" });
  if (who.is_ignored) badges.push({ t: "ignored", title: "spec [[ignore]]", cls: "text-fg-faint" });
  if (who.muted) badges.push({ t: "muted", title: "muted in address book", cls: "text-fg-faint" });
  if (badges.length === 0) return null;
  return (
    <span className="inline-flex gap-0.5">
      {badges.map((b) => (
        <span
          key={b.t}
          className={`rounded-sm border border-border px-0.5 text-3xs leading-[12px] ${b.cls}`}
          title={b.title}
        >
          {b.t}
        </span>
      ))}
    </span>
  );
}

export function TxLink({
  tx,
  urls,
  explorerTemplate,
  className = "",
}: {
  tx: string;
  urls?: Urls;
  explorerTemplate?: string;
  className?: string;
}) {
  const href = urls?.tx ?? explorerUrl(explorerTemplate, tx);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" className="num" title={tx}>
          {shortAddress(tx, 8, 6)}
        </a>
      ) : (
        <span className="num" title={tx}>
          {shortAddress(tx, 8, 6)}
        </span>
      )}
      <CopyButton text={tx} />
    </span>
  );
}
